import React, { useState, useCallback, useMemo } from "react";
import Grid from "@mui/material/Unstable_Grid2";
import { theme } from "@answersnow/pkg-theme";
import { TasksHeader } from "./TasksHeader";
import { TasksTable } from "./TasksTable";
import { TASK_TYPES } from "./constants";
import type { ClientTasksProps, Task, TaskType } from "./types";
import { useGetUserTask } from "hooks/api/user/useGetUserTask";
import { useGetUserUpcomingTask } from "hooks/api/user/useGetUserUpcomingTask";
import { format } from "date-fns";
import { convertTokens } from "@date-fns/upgrade/v2";
import type { Task as ApiTask, UpcomingTask as ApiUpcomingTask } from "api/types/users";

const EMPTY_TASKS: Record<TaskType, Task[]> = {
  [TASK_TYPES.CURRENT]: [],
  [TASK_TYPES.UPCOMING]: [],
  [TASK_TYPES.PAST]: [],
};


function formatTaskDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return format(new Date(dateStr), convertTokens("MMM D, YYYY h:mm A"));
  } catch {
    return dateStr;
  }
}

const UPCOMING_TASK_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});

function formatUpcomingTaskDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  try {
    return UPCOMING_TASK_DATE_FORMATTER.format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

function mapApiTaskToTask(apiTask: ApiTask): Task {
  return {
    id: String(apiTask.id),
    name: apiTask.display_name,
    date: formatTaskDate(apiTask.completed ? apiTask.completed_at : apiTask.created_at),
  };
}

function mapUpcomingApiTaskToTask(apiTask: ApiUpcomingTask): Task {
  return {
    id: `${apiTask.type}-${apiTask.trigger_date}-${apiTask.display_name}`,
    name: apiTask.display_name,
    date: formatUpcomingTaskDate(apiTask.trigger_date),
  };
}

/**
 * ClientTasks displays a filterable table of client tasks.
 * Allows filtering between current, upcoming, and past tasks.
 */
const ClientTasks: React.FC<ClientTasksProps> = ({
  userId,
  clientId,
  onTaskSelect,
}) => {
  const [selectedTab, setSelectedTab] = useState<TaskType>(TASK_TYPES.CURRENT);

  const { tasks: apiTasks, tasksLoading } = useGetUserTask(
    userId ?? "",
    clientId ? { client_id: Number(clientId) } : null
  );

  const { tasks: upcomingApiTasks, tasksLoading: upcomingTasksLoading } = useGetUserUpcomingTask(
    userId ?? "",
    clientId ? { client_id: Number(clientId) } : null
  );

  const tasks = useMemo<Record<TaskType, Task[]>>(() => {
    if (!apiTasks && !upcomingApiTasks) return EMPTY_TASKS;
    return {
      [TASK_TYPES.CURRENT]: (apiTasks ?? []).filter((t) => !t.completed).map(mapApiTaskToTask),
      [TASK_TYPES.UPCOMING]: (upcomingApiTasks ?? []).map(mapUpcomingApiTaskToTask),
      [TASK_TYPES.PAST]: (apiTasks ?? []).filter((t) => t.completed).map(mapApiTaskToTask),
    };
  }, [apiTasks, upcomingApiTasks]);

  const counts = useMemo<Record<TaskType, number>>(
    () => ({
      [TASK_TYPES.CURRENT]: tasks[TASK_TYPES.CURRENT].length,
      [TASK_TYPES.UPCOMING]: tasks[TASK_TYPES.UPCOMING].length,
      [TASK_TYPES.PAST]: tasks[TASK_TYPES.PAST].length,
    }),
    [tasks]
  );

  const currentTasks = useMemo(() => tasks[selectedTab], [tasks, selectedTab]);

  const handleTabChange = useCallback((newTab: TaskType) => {
    setSelectedTab(newTab);
  }, []);

  return (
    <Grid
      container
      component="section"
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        my: 7,
        pt: "16px",
        pb: "8px",
        px: "8px",
        gap: "16px",
        borderRadius: "16px",
        bgcolor: theme.palette.grey[50],
      }}
      data-testid="client-tasks"
      aria-labelledby="client-tasks-heading"
    >
      <TasksHeader selectedTab={selectedTab} counts={counts} onChange={handleTabChange} />
      <TasksTable tasks={currentTasks} selectedTab={selectedTab} loading={(selectedTab === TASK_TYPES.UPCOMING ? upcomingTasksLoading : tasksLoading) || !userId || !clientId} onTaskSelect={onTaskSelect} />
    </Grid>
  );
};

export default ClientTasks;
